import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import { PlacePrediction } from '../../../hooks/usePlacesAutocomplete';
import useToast from '../../../hooks/useToast';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectActiveList,
  selectActiveListCardOrder,
  selectCards,
  selectTripLocation,
} from '../../../redux/trip/trip.selectors';
import { addCardToList, removeCardFromList } from '../../../redux/trip/trip.slice';
import { CardID, CurrentTrip, TripCard } from '../../../redux/trip/trip.types';
import { getOrderedCards, isPlaceAlreadyInList } from '../../../redux/trip/trip.utils';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { Marker } from '../../../types/MapMarker';
import { getMarkersForCardsInListsAndExtendBounds } from '../../../utils/map-utils';
import { buildCardFromPlaceDetails } from '../../../utils/places-api.utils';
import SortableCard from '../../Card/SortableCard';
import MapComponent, { type MapPoiData } from '../../Map/Map';
import PlacesAutocomplete from '../../PlacesAutocomplete/PlacesAutocomplete';
import MobileTripListDefault from '../Itinerary/components/ItinerarySidebarPlaceholder';
import TripSidebar from '../TripSidebar/TripSidebar';
import TripSidebarMobile from '../TripSidebar/TripSidebarMobile';
import './TripPlayground.scss';

function devLog(message: string, data?: unknown) {
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.log(message, data);
  }
}

interface TripPlaygroundProps {
  trip: CurrentTrip;
  canEdit?: boolean;
  handleMarkerClick: (marker: Marker) => void;
}

function TripPlayground({ trip, canEdit, handleMarkerClick }: TripPlaygroundProps) {
  const dispatch = useAppDispatch();
  const { error: toastError } = useToast();
  const currentUser = useAppSelector(selectCurrentUser);
  const isMobile = useMobileMediaQuery();
  const activeList = useAppSelector(selectActiveList);
  const activeListCardOrder = useAppSelector(selectActiveListCardOrder);
  const cards = useAppSelector(selectCards);
  const location = useAppSelector(selectTripLocation);
  const mapRef = useRef<google.maps.Map | null>(null);
  const newMarker = useRef(new window.google.maps.Marker());

  const [isMapVisible, setIsMapVisible] = useState<boolean>(Boolean(canEdit));
  const [shouldFitBounds, setShouldFitBounds] = useState(true);
  const [highlightedCardId, setHighlightedCardId] = useState<CardID | null>(null);
  // Mirrors mapRef in state so the fit-bounds effect re-runs once the map has
  // loaded — a ref alone doesn't trigger a render, so markers computed before
  // load were never fitted until something else (e.g. a card hover) re-rendered.
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const setMapRef = useCallback((map: google.maps.Map | null) => {
    mapRef.current = map ?? null;
    setMapInstance(map ?? null);
    if (map) {
      setShouldFitBounds(true);
    }
  }, []);

  const orderedActiveListCards = useMemo(
    () => (activeList ? getOrderedCards(trip, activeList) : []),
    [trip, activeList]
  );

  // Maps googlePlaceId → list IDs that already contain that place (for POI popup checkbox pre-checking)
  const placeIdToListIds = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const list of trip.lists) {
      for (const cardId of list.cardOrder ?? []) {
        const googlePlaceId = cards?.[cardId]?.googlePlaceId;
        if (googlePlaceId) {
          if (!map[googlePlaceId]) {
            map[googlePlaceId] = [];
          }
          map[googlePlaceId].push(list.id);
        }
      }
    }
    return map;
  }, [trip.lists, cards]);

  const handleMapAddToList = useCallback(
    async (listId: string, poi: MapPoiData): Promise<void> => {
      if (!currentUser) {
        return;
      }
      const targetList = trip.lists.find((l) => l.id === listId);
      if (!targetList || !cards) {
        return;
      }
      const alreadyInList = isPlaceAlreadyInList(targetList, cards, poi.placeId);
      if (alreadyInList) {
        const cardId = (targetList.cardOrder ?? []).find(
          (id) => cards[id]?.googlePlaceId === poi.placeId
        );
        if (cardId) {
          dispatch(
            removeCardFromList({
              tripId: trip.id,
              listId,
              cardId,
              cardOrder: targetList.cardOrder ?? [],
            })
          );
        }
        return;
      }
      const card = await buildCardFromPlaceDetails(poi);
      dispatch(
        addCardToList({
          tripId: trip.id,
          listId,
          card: { ...card, creator: currentUser.uid },
          cardOrder: targetList.cardOrder ?? [],
        })
      );
    },
    [currentUser, trip.lists, trip.id, cards, dispatch]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-fit when the active list changes, not when its cardOrder updates
  useEffect(() => {
    setShouldFitBounds(true);
  }, [activeList?.id]);

  // Memoized so marker/bounds recalculation only runs when the active list, cards, or highlighted card actually change — not on every render.
  const { activeListMarkers, activeListMapBounds } = useMemo(() => {
    if (!activeList || !cards) {
      return { activeListMarkers: [] as Marker[], activeListMapBounds: null };
    }
    const b = new window.google.maps.LatLngBounds();
    const { markers } = getMarkersForCardsInListsAndExtendBounds(
      [activeList],
      cards,
      b,
      highlightedCardId
    );
    return { activeListMarkers: markers, activeListMapBounds: b };
  }, [activeList, cards, highlightedCardId]);

  useEffect(() => {
    if (shouldFitBounds && mapInstance && activeListMarkers.length && activeListMapBounds) {
      const map = mapInstance;
      map.fitBounds(activeListMapBounds);
      const zoom = map.getZoom();
      if (zoom && zoom > 14) {
        map.setZoom(14);
      }
      setShouldFitBounds(false);
    }
  }, [shouldFitBounds, mapInstance, activeListMarkers, activeListMapBounds]);

  const doesActiveListHaveCards = () => orderedActiveListCards.length > 0;

  const getCardCountText = (count: number): string => {
    return count === 1 ? '1 place' : `${count} places`;
  };

  const handleSuggestionMouseEnter = (suggestion: PlacePrediction) => {
    const map = mapRef.current;
    if (!isMapVisible || !map) {
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK || !results?.[0]) {
        devLog('Geocoder failed for hover preview:', status);
        return;
      }

      const latLng = results[0].geometry.location;
      newMarker.current.setPosition(latLng);
      newMarker.current.setMap(map);

      const bounds = map.getBounds();
      if (bounds && !bounds.contains(latLng)) {
        map.panTo(latLng);
      }
    });
  };

  const handleSuggestionMouseLeave = () => {
    if (!isMapVisible) {
      return;
    }

    newMarker.current.setMap(null);
  };

  const handleSuggestionSelect = (
    suggestion: PlacePrediction,
    sessionToken: string,
    usedNewApi: boolean
  ) => {
    const { place_id } = suggestion;
    newMarker.current.setMap(null);

    const sessionTokenToUse = usedNewApi ? sessionToken : undefined;

    buildCardFromPlaceDetails(
      { placeId: place_id, name: suggestion.description, lat: 0, lng: 0 },
      { sessionToken: sessionTokenToUse }
    )
      .then((card) => {
        // Guard: Ensure required state exists
        if (!cards || !activeList) {
          toastError('Unable to add place. Please select a list and try again.');
          return;
        }

        if (isPlaceAlreadyInList(activeList, cards, place_id)) {
          toastError(`This place has already been added to your ${activeList.name} list.`);
        } else if (currentUser && trip) {
          dispatch(
            addCardToList({
              tripId: trip.id,
              listId: activeList.id,
              card: { ...card, creator: currentUser.uid },
              cardOrder: activeListCardOrder || [],
            })
          );
        } else {
          toastError('Please sign in to add places.');
        }
      })
      .catch((error) => {
        devLog('Error fetching place details:', error);
        toastError(`Failed to add place. Please try again.`);
      });
  };

  const handleCardMouseEnter = (card: TripCard) => {
    if (!isMapVisible || !card.location) {
      return;
    }

    const { latitude, longitude } = card.location;
    const latLng = { lat: latitude, lng: longitude };
    if (mapRef?.current && !mapRef.current.getBounds()?.contains(latLng)) {
      mapRef.current.panTo(latLng);
    }
    setHighlightedCardId(card.id);
  };

  const handleCardMouseLeave = () => {
    if (!isMapVisible) {
      return;
    }

    setHighlightedCardId(null);
  };

  const hasCards = doesActiveListHaveCards();

  const mapTopClass = [
    'trip-map-top-container',
    isMapVisible && hasCards ? 'with-cards' : 'default',
    canEdit ? 'can-edit' : 'read-only',
  ].join(' ');

  const cardsClass = [
    'trip-cards-scrollable-container',
    isMapVisible ? 'with-map' : 'without-map',
    !isMapVisible && (canEdit ? 'can-edit' : 'read-only'),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className='trip-playground-container'>
      <div className='trip-sidebar-container'>
        {trip && isMobile ? (
          <>
            <TripSidebarMobile
              trip={trip}
              canEdit={canEdit}
              location={location}
              onSuggestionMouseEnter={handleSuggestionMouseEnter}
              onSuggestionMouseLeave={handleSuggestionMouseLeave}
              onSuggestionSelect={handleSuggestionSelect}
            />
            {trip.lists.length === 0 && <MobileTripListDefault />}
          </>
        ) : (
          <TripSidebar />
        )}
      </div>
      {canEdit && !isMobile && (
        <div className={mapTopClass}>
          {trip.lists.length > 0 && (
            <div className='trip-autocomplete-container'>
              <PlacesAutocomplete
                location={location}
                placeholder='Add a place or an address'
                className='playground-autocomplete'
                onSuggestionMouseEnter={handleSuggestionMouseEnter}
                onSuggestionMouseLeave={handleSuggestionMouseLeave}
                onSuggestionSelect={handleSuggestionSelect}
                shouldClearValueOnSelect
                activeList={activeList}
                cards={cards}
                showIcon
              />
              {!isMapVisible && doesActiveListHaveCards() && (
                <div className='card-count-inline'>
                  {getCardCountText(orderedActiveListCards.length)}
                </div>
              )}
            </div>
          )}
          {hasCards && (
            <Button
              className='button view-map-button'
              variant='outlined'
              onClick={() => setIsMapVisible(!isMapVisible)}
            >
              {isMapVisible ? 'Close Map' : 'View Map'}
            </Button>
          )}
        </div>
      )}
      {activeList && !isMobile && hasCards ? (
        <>
          {isMapVisible && (
            <div className='card-count-header'>
              {getCardCountText(orderedActiveListCards.length)}
            </div>
          )}
          <div className={cardsClass}>
            <SortableContext
              items={activeListCardOrder ?? []}
              strategy={verticalListSortingStrategy}
            >
              {orderedActiveListCards.map((card) => (
                <SortableCard
                  key={card.id}
                  card={card}
                  listId={activeList.id}
                  tripId={trip.id}
                  cardOrder={activeListCardOrder || []}
                  canEdit={canEdit}
                  canDelete={canEdit}
                  onCardMouseEnter={handleCardMouseEnter}
                  onCardMouseLeave={handleCardMouseLeave}
                  isLarge={!isMapVisible}
                />
              ))}
            </SortableContext>
          </div>
          {isMapVisible && !isMobile && (
            <div className='trip-with-cards-map-container'>
              <MapComponent
                ref={setMapRef}
                center={location}
                markers={activeListMarkers}
                markerOnClick={handleMarkerClick}
                showCurrentLocation={true}
                variant='playground'
                lists={trip.lists}
                placeIdToListIds={placeIdToListIds}
                onAddToList={handleMapAddToList}
              />
            </div>
          )}
        </>
      ) : (
        !isMobile && (
          <div className='trip-playground-map-container'>
            <MapComponent
              ref={setMapRef}
              center={location}
              showCurrentLocation={true}
              variant='playground'
              lists={trip.lists}
              placeIdToListIds={placeIdToListIds}
              onAddToList={handleMapAddToList}
            />
          </div>
        )
      )}
    </div>
  );
}

TripPlayground.displayName = 'TripPlayground';
export default TripPlayground;
